import React, { useMemo } from 'react';
import cn from 'classnames';

import { useTranslation } from '@lyearn/i18n';
import { CheckMark } from '@lyearn/icons';
import { Stack, Typography } from '@lyearn/ui';
import QuestionEntity from '@/modules/question/entity/Question';

import { getHTMLTextContent } from '../../../../QuizEditor/components/QuizEditorSidebar/views/QuestionsList/components/QuestionListItem/utils';
import { QuizNavigationSidebarItemProps } from './types';

const QuizNavigationSidebarItem: React.FC<QuizNavigationSidebarItemProps> = React.memo((props) => {
  const {
    question,
    isSelected,
    questionIndex,
    onClick: onClickProp,
    variant = 'player', //QP: remove
    endAdornment,
  } = props;
  const { t } = useTranslation();

  const QuestionTypeIcon = QuestionEntity.questionTypeIcon(question.questionType) ?? CheckMark;

  const html = useMemo(
    () =>
      question.questionText
        ? getHTMLTextContent(question.questionText)
        : t('QuizCreator:UntitledQuestion'),
    [question.questionText, t],
  );

  const onClick = React.useCallback(() => {
    if (onClickProp) {
      onClickProp(questionIndex);
    }
  }, [onClickProp, questionIndex]);

  return (
    <Stack
      className={cn(
        'rounded-8 px-12 py-[0.9rem]',
        {
          'bg-interactive-secondary': isSelected,
          'cursor-pointer hover:bg-bg-hover': onClickProp,
        },
        variant === 'player' ? 'py-[0.9rem]' : 'ps-8 py-12',
      )}
      direction="row"
      onClick={onClickProp ? onClick : undefined}>
      {variant === 'player' ? (
        <Typography
          className="pe-8"
          color={isSelected ? 'text-text-primary' : 'text-text-secondary'}
          variant="label-02">
          {`${questionIndex + 1}.`}
        </Typography>
      ) : (
        <Stack
          alignItems="center"
          className="mt-2 h-24 w-24 shrink-0 rounded-4 bg-bg-tertiary me-12"
          justifyContent="center">
          <QuestionTypeIcon className="h-18 w-18" fillColorToken="icon-primary" />
        </Stack>
      )}
      <Typography
        className="me-16"
        color={isSelected ? 'text-text-primary' : 'text-text-secondary'}
        numberOfLines={2}
        variant="body-short-02">
        {QuestionEntity.questionTextLabel(html)}
      </Typography>
      <Stack
        alignItems="center"
        className="mt-1 h-20 w-20 flex-shrink-0 ms-auto"
        justifyContent="center">
        {endAdornment}
      </Stack>
    </Stack>
  );
});

export default QuizNavigationSidebarItem;
